import type {
  AuditEntry,
  AuditFacets,
  AuditFilters,
  AnalysisRun,
  ApiKeyRecord,
  ApiSession,
  ChangeDiff,
  ChangeMatch,
  Collection,
  FileInfo,
  GitHubInstallation,
  GitHubRepository,
  Group,
  Hypothesis,
  Invitation,
  Lead,
  LeadDetail,
  ParseFailure,
  ProjectCollaborator,
  Organization,
  Project,
  RepositoryExplorerPage,
  RepositoryFocus,
  RepositorySavedView,
  RepositorySuggestion,
  RuleHistory,
  RunEvent,
  SearchResult,
  SourceRange,
  SymbolInfo,
  SymbolDetail,
  User,
} from './types'

type ErrorBody = { code?: string; message?: string; [key: string]: unknown }

export class ApiError extends Error {
  status: number
  code: string
  requestId?: string
  data: ErrorBody

  constructor(
    status: number,
    code: string,
    message: string,
    data: ErrorBody = {},
    requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.data = data
    this.requestId = requestId
  }
}

// `null` means the app has not established auth state yet and may fall back to
// the cookie. An empty string is an explicit clear after logout/session expiry.
let csrfToken: string | null = null

function cookie(name: string) {
  if (typeof document === 'undefined') return ''
  const prefix = `${encodeURIComponent(name)}=`
  const item = document.cookie.split('; ').find((part) => part.startsWith(prefix))
  if (!item) return ''

  try {
    return decodeURIComponent(item.slice(prefix.length))
  } catch {
    // A malformed cookie must not prevent the entire API client from starting.
    return ''
  }
}

export function setCsrfToken(value?: string | null) {
  csrfToken = value === undefined ? cookie('bug_csrf') : (value ?? '')
}

type RequestOptions = Omit<RequestInit, 'body'> & { body?: unknown; skipAuthRedirect?: boolean }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isAbortError(error: unknown) {
  return isRecord(error) && error.name === 'AbortError'
}

type ResponsePayload = { value: unknown; isJson: boolean }

async function responsePayload(response: Response): Promise<ResponsePayload> {
  const text = await response.text()
  if (!text) return { value: undefined, isJson: true }

  try {
    return { value: JSON.parse(text) as unknown, isJson: true }
  } catch {
    return { value: text, isJson: false }
  }
}

// Request validation failures arrive as a list of field errors; the first one is the message.
function validationMessage(errors: unknown[]) {
  const first = errors.find(isRecord)
  if (!first || typeof first.msg !== 'string') return undefined
  const message = first.msg.replace(/^Value error, /, '')
  const field = Array.isArray(first.loc)
    ? first.loc.filter((part) => typeof part === 'string' && part !== 'body').at(-1)
    : undefined
  if (typeof field !== 'string') return message
  const label = field.replaceAll('_', ' ')
  return `${label[0].toUpperCase()}${label.slice(1)}: ${message[0].toLowerCase()}${message.slice(1)}`
}

function errorBody(payload: unknown, fallback: string): ErrorBody {
  if (!isRecord(payload)) return { message: fallback }

  const detail = payload.detail
  if (isRecord(detail)) return detail
  if (typeof detail === 'string' && detail) return { message: detail }
  if (Array.isArray(detail)) {
    const message = validationMessage(detail)
    if (message) return { code: 'invalid_request', message, errors: detail }
  }

  const body: ErrorBody = {}
  if (typeof payload.code === 'string') body.code = payload.code
  if (typeof payload.message === 'string') body.message = payload.message
  return Object.keys(body).length ? body : { message: fallback }
}

function announceSessionExpiry(code: string, message: string) {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(
      'bug.returnTo',
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    )
  } catch {
    // Storage may be disabled. Navigation still needs to proceed.
  }
  window.dispatchEvent(new CustomEvent('bug:session-expired', { detail: { code, message } }))
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, skipAuthRedirect = false, ...requestInit } = options
  const method = (requestInit.method || 'GET').toUpperCase()
  const mutation = !['GET', 'HEAD', 'OPTIONS'].includes(method)
  const headers = new Headers(requestInit.headers)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  if (body !== undefined) headers.set('Content-Type', 'application/json')
  if (mutation) {
    const token = csrfToken === null ? cookie('bug_csrf') : csrfToken
    if (token) headers.set('X-CSRF-Token', token)
  }

  const encodedBody = body === undefined ? undefined : JSON.stringify(body)
  let response: Response
  try {
    response = await fetch(path, {
      ...requestInit,
      method,
      headers,
      credentials: 'include',
      body: encodedBody,
    })
  } catch (error) {
    if (isAbortError(error)) throw error
    const offline = typeof navigator !== 'undefined' && !navigator.onLine
    throw new ApiError(
      0,
      offline ? 'offline' : 'server_unavailable',
      offline ? 'You are offline' : 'The server is unavailable',
      {},
      undefined,
    )
  }
  const requestId = response.headers.get('x-request-id') || undefined
  if (!response.ok) {
    const payload = await responsePayload(response)
    const detail = errorBody(payload.value, response.statusText || 'Request failed')
    const code =
      detail.code ||
      (response.status === 403
        ? 'permission_denied'
        : response.status === 404
          ? 'not_found'
          : 'request_failed')
    const message = detail.message || response.statusText || 'Request failed'
    if (response.status === 401 && !skipAuthRedirect) announceSessionExpiry(code, message)
    throw new ApiError(response.status, code, message, detail, requestId)
  }

  const payload = await responsePayload(response)
  if (payload.value === undefined) return undefined as T
  if (!payload.isJson) {
    throw new ApiError(
      response.status,
      'invalid_response',
      'The server returned an invalid response',
      {},
      requestId,
    )
  }
  return payload.value as T
}

export function buildQuery(
  params: Record<string, string | number | boolean | string[] | undefined | null>,
) {
  const result = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    if (typeof value === 'number' && !Number.isFinite(value)) continue
    if (Array.isArray(value))
      value.filter((item) => item !== '').forEach((item) => result.append(key, item))
    else result.set(key, String(value))
  }
  const encoded = result.toString()
  return encoded ? `?${encoded}` : ''
}

async function collectPages<T>(load: (cursor?: string) => Promise<Collection<T>>) {
  const items: T[] = []
  let cursor: string | undefined
  let total = 0
  do {
    const page = await load(cursor)
    items.push(...page.items)
    total = page.total
    cursor = page.next_cursor || undefined
  } while (cursor)
  return { items, next_cursor: null, total } satisfies Collection<T>
}

export const api = {
  me: (signal?: AbortSignal) =>
    request<{ user: User; csrf_token: string }>('/api/v1/auth/me', {
      signal,
      skipAuthRedirect: true,
    }),
  login: (username: string, password: string) =>
    request<{ user: User; csrf_token: string }>('/api/v1/auth/login', {
      method: 'POST',
      body: { username, password },
      skipAuthRedirect: true,
    }),
  // A session the server already ended (password change, revocation) is still a clean sign-out.
  logout: () => request<void>('/api/v1/auth/logout', { method: 'POST', skipAuthRedirect: true }),
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/api/v1/auth/password', {
      method: 'POST',
      body: { current_password: currentPassword, new_password: newPassword },
    }),
  sessions: () => request<Collection<ApiSession>>('/api/v1/auth/sessions'),
  revokeSession: (id: string) => request<void>(`/api/v1/auth/sessions/${id}`, { method: 'DELETE' }),
  revokeAllSessions: () => request<void>('/api/v1/auth/sessions/revoke-all', { method: 'POST' }),
  apiKeys: () => request<{ items: ApiKeyRecord[]; total: number }>('/api/v1/api-keys'),
  createApiKey: (payload: {
    name: string
    org_id: string
    default_group_id: string
    access_level: ApiKeyRecord['access_level']
    expires_in_days?: number
  }) =>
    request<{ api_key: ApiKeyRecord; token: string }>('/api/v1/api-keys', {
      method: 'POST',
      body: payload,
    }),
  revokeApiKey: (id: string) => request<void>(`/api/v1/api-keys/${id}`, { method: 'DELETE' }),
  inspectInvite: (token: string) =>
    request<{
      invitation: {
        organization: string
        organization_role: string
        group_roles: unknown[]
        expires_at: string
      }
    }>(`/api/v1/auth/invitations/${encodeURIComponent(token)}`, { skipAuthRedirect: true }),
  acceptInviteNew: (token: string, username: string, password: string) =>
    request<{ user: User; csrf_token: string }>(
      `/api/v1/auth/invitations/${encodeURIComponent(token)}/accept-new`,
      { method: 'POST', body: { username, password }, skipAuthRedirect: true },
    ),
  acceptInvite: (token: string) =>
    request<{ accepted: boolean; org_id: string }>(
      `/api/v1/auth/invitations/${encodeURIComponent(token)}/accept`,
      { method: 'POST' },
    ),
  resetPassword: (token: string, password: string) =>
    request<void>(`/api/v1/auth/password-resets/${encodeURIComponent(token)}`, {
      method: 'POST',
      body: { password },
      skipAuthRedirect: true,
    }),
  githubStatus: () =>
    request<{ configured: boolean; installations: GitHubInstallation[] }>('/api/v1/github/status'),
  connectGithub: () =>
    request<{ authorization_url: string }>('/api/v1/github/connect', { method: 'POST' }),
  syncGithubInstallation: (id: string) =>
    request<{ repositories: GitHubRepository[] }>(`/api/v1/github/installations/${id}/sync`, {
      method: 'POST',
    }),
  githubRepositories: (installationId: string) =>
    request<{ repositories: GitHubRepository[] }>(
      `/api/v1/github/repositories${buildQuery({ installation_id: installationId })}`,
    ),

  organizations: (
    params: { q?: string; archived?: boolean; cursor?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) => request<Collection<Organization>>(`/api/v1/organizations${buildQuery(params)}`, { signal }),
  allOrganizations: (signal?: AbortSignal) =>
    collectPages((cursor) =>
      request<Collection<Organization>>(
        `/api/v1/organizations${buildQuery({ cursor, limit: 100 })}`,
        { signal },
      ),
    ),
  organization: (id: string) =>
    request<{ organization: Organization }>(`/api/v1/organizations/${id}`),
  createOrganization: (name: string, slug: string) =>
    request<{ organization: Organization }>('/api/v1/organizations', {
      method: 'POST',
      body: { name, slug },
    }),
  archiveOrganization: (id: string) =>
    request<{ organization: Organization }>(`/api/v1/organizations/${id}/archive`, {
      method: 'POST',
    }),
  restoreOrganization: (id: string) =>
    request<{ organization: Organization }>(`/api/v1/organizations/${id}/restore`, {
      method: 'POST',
    }),
  purgeOrganization: (id: string, confirmation: string) =>
    request<{ job_id: string }>(`/api/v1/admin/organizations/${id}/purge`, {
      method: 'POST',
      body: { confirmation },
    }),
  addOrganizationMember: (orgId: string, userId: string, role: string) =>
    request(`/api/v1/organizations/${orgId}/members`, {
      method: 'POST',
      body: { user_id: userId, role },
    }),
  updateOrganizationMember: (orgId: string, membershipId: string, role: string) =>
    request(`/api/v1/organizations/${orgId}/members/${membershipId}`, {
      method: 'PATCH',
      body: { role },
    }),
  removeOrganizationMember: (orgId: string, membershipId: string) =>
    request<void>(`/api/v1/organizations/${orgId}/members/${membershipId}`, { method: 'DELETE' }),
  groups: (orgId: string, q?: string, cursor?: string) =>
    request<Collection<Group>>(
      `/api/v1/organizations/${orgId}/groups${buildQuery({ q, cursor, limit: 100 })}`,
    ),
  allGroups: (orgId: string, q?: string) =>
    collectPages((cursor) =>
      request<Collection<Group>>(
        `/api/v1/organizations/${orgId}/groups${buildQuery({ q, cursor, limit: 100 })}`,
      ),
    ),
  createGroup: (orgId: string, name: string, slug: string) =>
    request<{ group: Group }>(`/api/v1/organizations/${orgId}/groups`, {
      method: 'POST',
      body: { name, slug },
    }),
  archiveGroup: (orgId: string, groupId: string) =>
    request<{ group: Group }>(`/api/v1/organizations/${orgId}/groups/${groupId}/archive`, {
      method: 'POST',
    }),
  restoreGroup: (orgId: string, groupId: string) =>
    request<{ group: Group }>(`/api/v1/organizations/${orgId}/groups/${groupId}/restore`, {
      method: 'POST',
    }),
  orgMembers: (orgId: string, q?: string, forGroupId?: string, cursor?: string) =>
    request<Collection<{ id: string; role: string; user: User }>>(
      `/api/v1/organizations/${orgId}/members${buildQuery({ q, for_group_id: forGroupId, cursor, limit: 100 })}`,
    ),
  allOrgMembers: (orgId: string, q?: string, forGroupId?: string) =>
    collectPages((cursor) =>
      request<Collection<{ id: string; role: string; user: User }>>(
        `/api/v1/organizations/${orgId}/members${buildQuery({ q, for_group_id: forGroupId, cursor, limit: 100 })}`,
      ),
    ),
  groupMembers: (orgId: string, groupId: string) =>
    request<Collection<{ id: string; role: string; user: User }>>(
      `/api/v1/organizations/${orgId}/groups/${groupId}/members`,
    ),
  addGroupMember: (orgId: string, groupId: string, userId: string, role: string) =>
    request(`/api/v1/organizations/${orgId}/groups/${groupId}/members`, {
      method: 'POST',
      body: { user_id: userId, role },
    }),
  updateGroupMember: (orgId: string, groupId: string, membershipId: string, role: string) =>
    request(`/api/v1/organizations/${orgId}/groups/${groupId}/members/${membershipId}`, {
      method: 'PATCH',
      body: { role },
    }),
  removeGroupMember: (orgId: string, groupId: string, membershipId: string) =>
    request<void>(`/api/v1/organizations/${orgId}/groups/${groupId}/members/${membershipId}`, {
      method: 'DELETE',
    }),
  invitations: (orgId: string, cursor?: string) =>
    request<Collection<Invitation>>(
      `/api/v1/organizations/${orgId}/invitations${buildQuery({ cursor, limit: 100 })}`,
    ),
  createInvitation: (
    orgId: string,
    organizationRole: string,
    groupRoles: { group_id: string; role: string }[],
  ) =>
    request<{ invitation: Invitation; token: string; path: string }>(
      `/api/v1/organizations/${orgId}/invitations`,
      { method: 'POST', body: { organization_role: organizationRole, group_roles: groupRoles } },
    ),
  revokeInvitation: (orgId: string, invitationId: string) =>
    request<void>(`/api/v1/organizations/${orgId}/invitations/${invitationId}`, {
      method: 'DELETE',
    }),
  audit: (orgId: string, filters: AuditFilters = {}, cursor?: string) =>
    request<Collection<AuditEntry> & { facets: AuditFacets }>(
      `/api/v1/organizations/${orgId}/audit${buildQuery({ ...filters, cursor, limit: 100 })}`,
    ),

  projects: (
    params: {
      org_id?: string
      group_id?: string
      q?: string
      status?: string
      review_state?: string
      archived?: boolean
      stale_days?: number
      sort?: string
      cursor?: string
      limit?: number
    } = {},
    signal?: AbortSignal,
  ) =>
    request<Collection<Project>>(`/api/v1/projects${buildQuery({ limit: 50, ...params })}`, {
      signal,
    }),
  repositoryExplorer: (
    params: {
      org_id: string
      q?: string
      sort?: string
      cursor?: string
      limit?: number
    },
    signal?: AbortSignal,
  ) =>
    request<RepositoryExplorerPage>(
      `/api/v1/repository-explorer${buildQuery({ limit: 50, ...params })}`,
      { signal },
    ),
  repositoryFocus: (orgId: string, signal?: AbortSignal) =>
    request<RepositoryFocus>(`/api/v1/repository-explorer/focus${buildQuery({ org_id: orgId })}`, {
      signal,
    }),
  repositorySuggestions: (orgId: string, q: string, signal?: AbortSignal) =>
    request<{ items: RepositorySuggestion[] }>(
      `/api/v1/repository-explorer/suggestions${buildQuery({ org_id: orgId, q })}`,
      { signal },
    ),
  updateRepositoryPreference: (
    projectId: string,
    payload: { pinned?: boolean; opened?: boolean },
  ) =>
    request<{
      preference: { project_id: string; pinned: boolean; last_opened_at?: string | null }
    }>(`/api/v1/repository-explorer/projects/${projectId}/preference`, {
      method: 'PATCH',
      body: payload,
    }),
  repositoryViews: (orgId: string) =>
    request<{ items: RepositorySavedView[]; total: number }>(
      `/api/v1/repository-explorer/views${buildQuery({ org_id: orgId })}`,
    ),
  createRepositoryView: (payload: { org_id: string; name: string; query: string; sort: string }) =>
    request<{ view: RepositorySavedView }>('/api/v1/repository-explorer/views', {
      method: 'POST',
      body: payload,
    }),
  deleteRepositoryView: (viewId: string) =>
    request<void>(`/api/v1/repository-explorer/views/${viewId}`, { method: 'DELETE' }),
  project: (id: string, signal?: AbortSignal) =>
    request<{ project: Project }>(`/api/v1/projects/${id}`, { signal }),
  projectCollaborators: (id: string, cursor?: string, signal?: AbortSignal) =>
    request<Collection<ProjectCollaborator>>(
      `/api/v1/projects/${id}/collaborators${buildQuery({ cursor, limit: 100 })}`,
      { signal },
    ),
  createProject: (payload: {
    org_id: string
    group_id: string
    url?: string
    github_repository_id?: string
    ref?: string
    analysis_settings: Record<string, unknown>
    start_run: boolean
  }) =>
    request<{ project: Project; run?: AnalysisRun }>('/api/v1/projects', {
      method: 'POST',
      body: payload,
    }),
  updateProject: (
    id: string,
    payload: Partial<{
      group_id: string
      requested_ref: string
      analysis_settings: Record<string, unknown>
    }>,
  ) => request<{ project: Project }>(`/api/v1/projects/${id}`, { method: 'PATCH', body: payload }),
  archiveProject: (id: string) =>
    request<{ project: Project }>(`/api/v1/projects/${id}/archive`, { method: 'POST' }),
  restoreProject: (id: string) =>
    request<{ project: Project }>(`/api/v1/projects/${id}/restore`, { method: 'POST' }),
  purgeProject: (id: string, confirmation: string) =>
    request<{ job_id: string }>(`/api/v1/projects/${id}/purge`, {
      method: 'POST',
      body: { confirmation },
    }),
  runs: (projectId: string) =>
    request<Collection<AnalysisRun>>(`/api/v1/projects/${projectId}/runs`),
  run: (runId: string, signal?: AbortSignal) =>
    request<{ run: AnalysisRun }>(`/api/v1/runs/${runId}`, { signal }),
  createRun: (projectId: string, requestedRef?: string) =>
    request<{ run: AnalysisRun }>(`/api/v1/projects/${projectId}/runs`, {
      method: 'POST',
      body: { requested_ref: requestedRef || null },
    }),
  cancelRun: (runId: string) =>
    request<{ run: AnalysisRun }>(`/api/v1/runs/${runId}/cancel`, { method: 'POST' }),
  retryRun: (runId: string) =>
    request<{ run: AnalysisRun }>(`/api/v1/runs/${runId}/retry`, { method: 'POST' }),
  compareRuns: (runId: string, otherRunId: string) =>
    request<{
      from: AnalysisRun
      to: AnalysisRun
      summary_changes: Record<string, { from: unknown; to: unknown; delta?: number | null }>
      coverage_changes: Record<string, { from: unknown; to: unknown }>
      lead_changes: { added: string[]; removed: string[]; unchanged: number }
    }>(`/api/v1/runs/${runId}/compare/${otherRunId}`),
  indexReport: (runId: string) =>
    request<{
      run_id: string
      engine_version: string
      schema_version: number
      coverage: AnalysisRun['coverage']
      summary: AnalysisRun['summary']
      qualification: string
    }>(`/api/v1/runs/${runId}/index-report`),
  files: (
    runId: string,
    params: { prefix?: string; language?: string; cursor?: string; limit?: number } = {},
    signal?: AbortSignal,
  ) =>
    request<Collection<FileInfo>>(
      `/api/v1/runs/${runId}/files${buildQuery({ limit: 250, ...params })}`,
      { signal },
    ),
  source: (runId: string, path: string, start: number, end: number, signal?: AbortSignal) =>
    request<SourceRange>(`/api/v1/runs/${runId}/source${buildQuery({ path, start, end })}`, {
      signal,
    }),
  symbols: (
    runId: string,
    params: {
      file?: string
      q?: string
      /** Only symbols whose body contains this line. */
      line?: number
      routes_only?: boolean
      cursor?: string
      limit?: number
    } = {},
    signal?: AbortSignal,
  ) =>
    request<Collection<SymbolInfo>>(
      `/api/v1/runs/${runId}/symbols${buildQuery({ limit: 250, ...params })}`,
      { signal },
    ),
  symbol: (runId: string, symbolId: string, signal?: AbortSignal) =>
    request<SymbolDetail>(`/api/v1/runs/${runId}/symbols/${symbolId}`, { signal }),
  leads: (
    runId: string,
    params: {
      family?: string[]
      access?: string[]
      lead_type?: string[]
      status?: string[]
      validated?: boolean
      min_confidence?: number
      min_priority?: number
      tier?: 'security' | 'investigation' | 'hardening' | 'suppressed'
      cursor?: string
      limit?: number
    } = {},
    signal?: AbortSignal,
  ) =>
    request<Collection<Lead>>(
      `/api/v1/runs/${runId}/leads${buildQuery({ limit: 50, ...params })}`,
      { signal },
    ),
  ruleHistory: (runId: string, signal?: AbortSignal) =>
    request<{ items: RuleHistory[] }>(`/api/v1/runs/${runId}/rule-history`, { signal }),
  reviewRule: (
    runId: string,
    payload: {
      rule_key: string
      tier: 'security' | 'investigation' | 'hardening'
      status: 'testing' | 'confirmed' | 'dismissed'
      dismissal_reason?: string | null
      comment?: string | null
    },
  ) =>
    request<{ updated: number; lead_ids: string[] }>(`/api/v1/runs/${runId}/rule-reviews`, {
      method: 'POST',
      body: payload,
    }),
  lead: (leadId: string, signal?: AbortSignal) =>
    request<LeadDetail>(`/api/v1/leads/${leadId}`, { signal }),
  reviewLead: (
    leadId: string,
    payload: {
      status: string
      assignee_id?: string | null
      tags?: string[]
      dismissal_reason?: string | null
      comment?: string | null
    },
  ) =>
    request<{ lead: Lead }>(`/api/v1/leads/${leadId}/review`, {
      method: 'PUT',
      body: { tags: [], ...payload },
    }),
  changes: (runId: string, cursor?: string, signal?: AbortSignal) =>
    request<Collection<ChangeMatch>>(
      `/api/v1/runs/${runId}/changes${buildQuery({ cursor, limit: 50 })}`,
      { signal },
    ),
  changeDiff: (runId: string, changeId: string, signal?: AbortSignal) =>
    request<ChangeDiff>(`/api/v1/runs/${runId}/changes/${changeId}/diff`, { signal }),
  parseFailures: (runId: string, cursor?: string) =>
    request<Collection<ParseFailure>>(
      `/api/v1/runs/${runId}/parse-failures${buildQuery({ cursor, limit: 100 })}`,
    ),
  hypotheses: (runId: string, includeArchived = false, signal?: AbortSignal) =>
    collectPages((cursor) =>
      request<Collection<Hypothesis>>(
        `/api/v1/runs/${runId}/hypotheses${buildQuery({ include_archived: includeArchived, cursor, limit: 100 })}`,
        { signal },
      ),
    ),
  createHypothesis: (runId: string, payload: Partial<Hypothesis> & { title: string }) =>
    request<{ hypothesis: Hypothesis }>(`/api/v1/runs/${runId}/hypotheses`, {
      method: 'POST',
      body: payload,
    }),
  updateHypothesis: (id: string, payload: Partial<Hypothesis> & { version: number }) =>
    request<{ hypothesis: Hypothesis }>(`/api/v1/hypotheses/${id}`, {
      method: 'PATCH',
      body: payload,
    }),
  reassignHypothesis: (
    id: string,
    payload: {
      version: number
      target_run_id: string
      lead_ids: string[]
      anchors: Hypothesis['anchors']
    },
  ) =>
    request<{ hypothesis: Hypothesis }>(`/api/v1/hypotheses/${id}/reassign`, {
      method: 'POST',
      body: payload,
    }),
  purgeHypothesis: (id: string, confirmation: string) =>
    request<void>(`/api/v1/hypotheses/${id}/purge`, { method: 'POST', body: { confirmation } }),
  search: (runId: string, q: string, kinds: string[], signal?: AbortSignal) =>
    request<Collection<SearchResult>>(`/api/v1/runs/${runId}/search${buildQuery({ q, kinds })}`, {
      signal,
    }),

  adminUsers: (q?: string, platformRole?: string, active?: boolean, cursor?: string) =>
    request<
      Collection<
        User & {
          memberships: { organizations?: unknown[]; groups?: unknown[] }
          active_sessions: number
        }
      >
    >(
      `/api/v1/admin/users${buildQuery({ q, platform_role: platformRole, active, cursor, limit: 100 })}`,
    ),
  allAdminUsers: () =>
    collectPages((cursor) =>
      request<
        Collection<
          User & {
            memberships: { organizations?: unknown[]; groups?: unknown[] }
            active_sessions: number
          }
        >
      >(`/api/v1/admin/users${buildQuery({ cursor, limit: 100 })}`),
    ),
  updateUser: (id: string, payload: Partial<Pick<User, 'platform_role' | 'is_active'>>) =>
    request<{ user: User }>(`/api/v1/admin/users/${id}`, { method: 'PATCH', body: payload }),
  revokeUserSessions: (id: string) =>
    request<{ revoked: number; self: boolean }>(`/api/v1/admin/users/${id}/revoke-sessions`, {
      method: 'POST',
    }),
  createReset: (id: string) =>
    request<{ reset: { id: string; expires_at: string; path: string }; token: string }>(
      `/api/v1/admin/users/${id}/password-reset`,
      { method: 'POST' },
    ),
  purgeUser: (id: string, confirmation: string) =>
    request<void>(`/api/v1/admin/users/${id}/purge`, { method: 'POST', body: { confirmation } }),
}

export function subscribeRun(
  runId: string,
  onEvent: (event: RunEvent) => void,
  onError: () => void,
) {
  let stream: EventSource
  try {
    stream = new EventSource(`/api/v1/runs/${encodeURIComponent(runId)}/events`, {
      withCredentials: true,
    })
  } catch {
    onError()
    return () => undefined
  }

  stream.addEventListener('run', (raw) => {
    try {
      const event = JSON.parse((raw as MessageEvent).data) as RunEvent
      onEvent(event)
      if (['ready', 'failed', 'cancelled', 'interrupted', 'purged'].includes(event.state))
        stream.close()
    } catch {
      onError()
    }
  })
  stream.onerror = onError
  return () => stream.close()
}
